import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  Divider,
} from "@material-ui/core";
import { Add, Delete, Info, Group, AttachFile, Save, Send } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";

import {
  createRegistration,
  updateInternalResearch,
  submitRegistrationInternal,
  filterInternalResearchV2,
} from "../../../../reducer/internalResearch/action";
import { uploadMultiFile } from "../../../../reducer/file/action";
import { notification } from "../../../../reducer/home/action";
import { KPTextfield } from "../../../../components/KPTComponents";
import { INSTITUTIONAL_PROJECT } from "../../../../constants/selectOptions";
import MultiFileManager from "../../../File/MultiFileManager/MultiFileManager";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";
import InternalResearchHistory from "../../utils/InternalResearchHistory";
import {
  INT_STATUS_REGISTRATION_DRAFT,
  INT_STATUS_MOD_ADJUST_REQUEST,
  INT_STATUS_ADMIN_ADJUST_REQUEST,
  INT_STATUS_SELECTION_COUNCIL_ADJUST_REQUEST,
  INT_DURATION_DEFAULT,
  INT_DURATION_OPTIONS,
  INTERNAL_RESEARCH_STATUS_LABEL,
} from "../../../../constants/internalResearchStatus";

// RegistrationForm v2 (refactor 2026-05-15) — gộp đăng ký đề tài + thành viên.
// Mode: create | edit | view (history-only). Khác với form v1 (DefaultForm, đã
// xoá 2026-08-27) ở chỗ dispatch action mới (createRegistration /
// updateInternalResearch / submitRegistrationInternal) và đồng bộ với state
// machine 30-status.
export default function RegistrationForm({ detail, mode = "create", onDone }) {
  const dispatch = useDispatch();
  const profileList   = useSelector((s) => select(s, "profileReducer", "list")) || [];
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));
  // Đề tài Cơ sở dùng store key "departments" (đã load qua InstitutionalProjectButton →
  // getAllDepartments). Module SV thì dùng "faculties" — khác store key.
  const departments   = useSelector((s) => select(s, "departmentReducer", "departments")) || [];

  // Chuẩn hoá status (có thể là string hoặc [{value,...}]).
  const extractStatus = (d) => {
    const s = d?.status;
    if (!s) return "";
    if (typeof s === "string") {
      const parts = s.split(",");
      return parts[parts.length - 1];
    }
    if (Array.isArray(s) && s.length > 0) {
      const last = s[s.length - 1];
      return typeof last === "object" ? last?.value : last;
    }
    return "";
  };
  const currentStatus = extractStatus(detail);

  const isView = mode === "view";
  // Refactor 2026-05-21: thêm SELECTION_COUNCIL_ADJUST_REQUEST — HĐ#1 yêu cầu
  // CN sửa thông tin đăng ký + nộp lại. Trước đó form ở status này bị render
  // read-only (không có nút Lưu/Nộp).
  const isAdjustRequest =
    currentStatus === INT_STATUS_MOD_ADJUST_REQUEST ||
    currentStatus === INT_STATUS_ADMIN_ADJUST_REQUEST ||
    currentStatus === INT_STATUS_SELECTION_COUNCIL_ADJUST_REQUEST;
  const canEdit =
    !isView &&
    (mode === "create" ||
      currentStatus === INT_STATUS_REGISTRATION_DRAFT ||
      isAdjustRequest);

  const parseUrls = (s) => (s ? String(s).split("||").filter(Boolean) : []);

  // Refactor 2026-05-21: startTime/endTime — default startTime=hôm nay,
  // endTime = start + durationMonths. Helpers convert giữa input date HTML +
  // ISO RFC3339 cho BE Go time.Time.
  const todayISO = () => new Date().toISOString().substring(0, 10);
  const toDateInput = (raw) => {
    if (!raw) return "";
    const s = String(raw).substring(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
  };
  const addMonthsISO = (startISO, months) => {
    if (!startISO) return "";
    const d = new Date(startISO);
    if (isNaN(d.getTime())) return "";
    d.setMonth(d.getMonth() + Number(months || 0));
    return d.toISOString().substring(0, 10);
  };
  const dateInputToISO = (s) => (s ? `${s}T00:00:00Z` : null);

  // status: KHÔNG để trong `values` vì detail?.status có thể là string HOẶC
  // array `[{value, label, bgcolor}]` (sau khi table.rowRender wrap lại). Lúc
  // setState array sẽ break `String#endsWith` ở status badge. Dùng
  // `currentStatus` (string đã chuẩn hoá) cho mọi nơi cần đọc trạng thái.
  const [values, setValues] = useState(() => {
    const start = toDateInput(detail?.startTime) || todayISO();
    return {
      id: detail?.id || 0,
      title:          detail?.title || "",
      vietnameseName: detail?.vietnameseName || "",
      englishName:    detail?.englishName    || "",
      researchType:   detail?.researchType   || "",
      schoolYear:     detail?.schoolYear     || new Date().getFullYear(),
      cost:           detail?.cost           || 0,
      expectedResult: detail?.expectedResult || "",
      departmentId:   detail?.departmentId   || 0,
      durationMonths: detail?.durationMonths || INT_DURATION_DEFAULT,
      startTime:      start,
      endTime:        toDateInput(detail?.endTime) || addMonthsISO(start, detail?.durationMonths || INT_DURATION_DEFAULT),
    };
  });
  // Helper: build participant entry từ profileDetail (logged-in user) — dùng
  // auto-add chairman khi tạo mới. Đảm bảo profileId được set để filter SQL
  // join `participants.profile_id` match được, nếu không list sẽ rỗng sau reload.
  const buildSelfParticipant = (role = "main") => ({
    profileId:    profileDetail?.id || 0,
    name:         profileDetail?.name || "",
    email:        profileDetail?.email || "",
    degree:       profileDetail?.degree || "",
    academicRank: profileDetail?.academicRank || "",
    department:   profileDetail?.departmentProfile?.[0]?.department?.name || "",
    researchRole: role,
    isPhenikaa:   true,
  });
  const [participants, setParticipants] = useState(() => {
    if (detail?.participants?.length) return detail.participants;
    if (mode === "create" && profileDetail?.id) return [buildSelfParticipant("main")];
    return [];
  });
  const [presentationUrls, setPresentationUrls]       = useState(parseUrls(detail?.presentationFileLink));
  const [presentationNewFiles, setPresentationNewFiles] = useState([]);
  const [statisticUrls, setStatisticUrls]             = useState(parseUrls(detail?.statisticPlanFileLink));
  const [statisticNewFiles, setStatisticNewFiles]     = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!detail) return;
    setValues((v) => {
      const start = toDateInput(detail.startTime) || v.startTime || todayISO();
      return {
        ...v,
        id: detail.id || 0,
        title:          detail.title || "",
        vietnameseName: detail.vietnameseName || "",
        englishName:    detail.englishName || "",
        researchType:   detail.researchType || "",
        schoolYear:     detail.schoolYear || v.schoolYear,
        cost:           detail.cost || 0,
        expectedResult: detail.expectedResult || "",
        departmentId:   detail.departmentId || 0,
        durationMonths: detail.durationMonths || INT_DURATION_DEFAULT,
        startTime:      start,
        endTime:        toDateInput(detail.endTime) || addMonthsISO(start, detail.durationMonths || v.durationMonths),
      };
    });
    setParticipants(detail.participants || []);
    setPresentationUrls(parseUrls(detail.presentationFileLink));
    setPresentationNewFiles([]);
    setStatisticUrls(parseUrls(detail.statisticPlanFileLink));
    setStatisticNewFiles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  // Auto-add chairman khi create mode + profileDetail load sau lần render đầu
  // (race condition: profileDetail thường load async sau khi component mount).
  useEffect(() => {
    if (mode !== "create") return;
    if (participants.length > 0) return;
    if (!profileDetail?.id) return;
    setParticipants([buildSelfParticipant("main")]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDetail?.id, mode]);

  const handleChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const commitFiles = async (existingUrls, newFiles, setUrls, setNewFiles) => {
    if (!newFiles || newFiles.length === 0) return existingUrls.join("||");
    const ownerId = profileDetail?.id || 0;
    const result = await dispatch(uploadMultiFile(ownerId, newFiles, "internalResearch"));
    if (result?.success && Array.isArray(result.data)) {
      const merged = [...existingUrls, ...result.data];
      setUrls(merged);
      setNewFiles([]);
      return merged.join("||");
    }
    return existingUrls.join("||");
  };

  const addParticipant = () =>
    setParticipants([
      ...participants,
      { name: "", profileId: 0, researchRole: participants.length === 0 ? "main" : "other", isPhenikaa: true },
    ]);
  const removeParticipant = (idx) => setParticipants(participants.filter((_, i) => i !== idx));
  const updateParticipant = (idx, patch) =>
    setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const validate = () => {
    if (!values.vietnameseName?.trim()) return "Vui lòng nhập Tên đề tài tiếng Việt.";
    if (!values.schoolYear)              return "Vui lòng nhập Năm học.";
    if (participants.length === 0)       return "Phải có ít nhất 1 thành viên (chủ nhiệm).";
    const chairman = participants.find((p) => p.researchRole === "main");
    if (!chairman) return "Phải chỉ định 1 chủ nhiệm đề tài (researchRole='main').";
    // Chairman bắt buộc là cán bộ nội bộ có profileId — nếu không, filter
    // SQL `participants.profile_id` join sẽ miss và đề tài không hiện trong
    // danh sách cá nhân sau khi reload.
    if (chairman.isPhenikaa !== false && !chairman.profileId) {
      return "Chủ nhiệm đề tài phải chọn từ danh sách cán bộ nội bộ (cần profileId).";
    }
    return null;
  };

  const buildPayload = (presentationStr, statisticStr) => ({
    ...values,
    startTime: dateInputToISO(values.startTime),
    endTime:   dateInputToISO(values.endTime),
    presentationFileLink: presentationStr,
    statisticPlanFileLink: statisticStr,
    participants,
    // Status: Lưu nháp ép về `registration_draft` (hoặc giữ adjust_request);
    // Nộp đăng ký sẽ được service `submitRegistrationInternal` set thành
    // `registration_submitted` sau khi update xong.
  });

  // refetchList — đồng bộ list sau action thành công. INSERT/UPDATE_SUCCESS đã
  // merge entity vào list local, nhưng entity FE gửi có thể thiếu field BE
  // compute (vd department, generated keys). Re-filter đảm bảo data fresh.
  const refetchList = () => {
    if (!profileDetail?.id) return;
    const sy = values.schoolYear || new Date().getFullYear();
    dispatch(filterInternalResearchV2({ profileId: profileDetail.id.toString(), schoolYear: sy }));
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      dispatch(notification({ type: "warning", message: err }));
      return;
    }
    setSaving(true);
    try {
      const presStr = await commitFiles(presentationUrls, presentationNewFiles, setPresentationUrls, setPresentationNewFiles);
      const statStr = await commitFiles(statisticUrls, statisticNewFiles, setStatisticUrls, setStatisticNewFiles);
      const payload = buildPayload(presStr, statStr);

      if (mode === "create") {
        // Lưu nháp lần đầu: ép status draft (BE default cũng vậy, ép thêm cho rõ).
        const entity = await dispatch(createRegistration({ ...payload, status: INT_STATUS_REGISTRATION_DRAFT }));
        if (entity) {
          refetchList();
          if (onDone) onDone();
        }
      } else {
        // Edit: giữ status hiện tại trừ khi đang adjust_request — vẫn để status không đổi
        // để admin/mod thấy được là user đang sửa.
        const ok = await dispatch(updateInternalResearch({ ...payload, id: values.id }));
        if (ok) {
          refetchList();
          if (onDone) onDone();
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      dispatch(notification({ type: "warning", message: err }));
      return;
    }
    setSaving(true);
    try {
      const presStr = await commitFiles(presentationUrls, presentationNewFiles, setPresentationUrls, setPresentationNewFiles);
      const statStr = await commitFiles(statisticUrls, statisticNewFiles, setStatisticUrls, setStatisticNewFiles);
      const payload = buildPayload(presStr, statStr);

      let id = values.id;
      if (mode === "create" || !id) {
        const entity = await dispatch(createRegistration({ ...payload, status: INT_STATUS_REGISTRATION_DRAFT }));
        if (!entity) return;
        id = entity.id;
      } else {
        const ok = await dispatch(updateInternalResearch({ ...payload, id }));
        if (!ok) return;
      }
      const ok = await dispatch(submitRegistrationInternal(id));
      if (ok) {
        refetchList();
        if (onDone) onDone();
      }
    } finally {
      setSaving(false);
    }
  };

  // Status badge — luôn lấy từ `currentStatus` (đã chuẩn hoá string ở đầu
  // component). `mode=create` chưa có status → fallback `registration_draft`.
  const displayStatus = currentStatus || (mode === "create" ? INT_STATUS_REGISTRATION_DRAFT : "");
  const statusBadgeBg = (() => {
    const s = String(displayStatus || "");
    if (s.endsWith("_rejected") || s === "stop_accepted") return "#e00000";
    if (s === "registration_draft") return "#243474";
    if (s.includes("accepted") || s === "conducted" || s === "conducted_phase2") return "#60ae7d";
    if (!s) return "#808080";
    return "#f46423";
  })();
  const statusLabel = INTERNAL_RESEARCH_STATUS_LABEL[displayStatus] || displayStatus || "Chưa xác định";

  return (
    <Box padding="16px 24px">
      {/* Status badge — luôn hiển thị (cả 3 mode) để user biết đang ở giai đoạn nào. */}
      <Box display="flex" alignItems="center" gridGap={8} marginBottom={1}>
        <Typography variant="body2" color="textSecondary">Trạng thái:</Typography>
        <Chip
          size="small"
          label={statusLabel}
          style={{ backgroundColor: statusBadgeBg, color: "#fff", height: 22, fontSize: 12 }}
        />
      </Box>

      {/* History phía trên cho view mode (đề tài đã có lịch sử) */}
      {isView && <InternalResearchHistory detail={detail} />}

      <SectionCard icon={<Info />} title="Thông tin đề tài" marginTop={isView ? 2 : 0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <KPTextfield
              id="vietnameseName"
              label="Tên đề tài tiếng Việt"
              value={values.vietnameseName}
              required={true}
              disabled={!canEdit}
              handleChangeForm={(_id, val) => handleChange("vietnameseName", val)}
            />
          </Grid>
          <Grid item xs={12}>
            <KPTextfield
              id="englishName"
              label="Tên đề tài tiếng Anh"
              value={values.englishName}
              disabled={!canEdit}
              handleChangeForm={(_id, val) => handleChange("englishName", val)}
            />
          </Grid>
          <Grid item xs={6}>
            <KPTextfield
              id="researchType"
              type="select"
              label="Loại nghiên cứu"
              value={values.researchType}
              options={INSTITUTIONAL_PROJECT.type}
              disabled={!canEdit}
              handleChangeForm={(_id, val) => handleChange("researchType", val)}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 500 }}>
              Năm học
            </Typography>
            {/* KPTextfield type="number" có bug getRange(undefined,undefined,...) → NaN
                (xem note refactor 2026-05-14 cho điểm chấm SV). Dùng MUI
                TextField + clamp manual để user nhập được. */}
            <TextField
              fullWidth size="small" variant="outlined" type="number"
              value={values.schoolYear}
              disabled={!canEdit}
              inputProps={{ min: 2000, max: 9999, step: 1 }}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") { handleChange("schoolYear", 0); return; }
                const n = parseInt(v, 10);
                if (!isNaN(n)) handleChange("schoolYear", n);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 500 }}>
              Thời lượng
            </Typography>
            <Select
              fullWidth variant="outlined" margin="dense"
              value={values.durationMonths}
              disabled={!canEdit}
              onChange={(e) => {
                const newDur = Number(e.target.value) || INT_DURATION_DEFAULT;
                setValues((cur) => ({
                  ...cur,
                  durationMonths: newDur,
                  endTime: cur.startTime ? addMonthsISO(cur.startTime, newDur) : cur.endTime,
                }));
              }}
            >
              {INT_DURATION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </Grid>
          {/* Refactor 2026-05-21: ngày bắt đầu / kết thúc đề tài Cơ sở */}
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 500 }}>
              Ngày bắt đầu <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="date"
              value={values.startTime || ""}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                const v = e.target.value;
                setValues((cur) => ({
                  ...cur,
                  startTime: v,
                  endTime: v ? addMonthsISO(v, cur.durationMonths) : cur.endTime,
                }));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 500 }}>
              Ngày kết thúc <span style={{ color: "#c62828" }}>*</span>
              <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                (auto tính, có thể sửa)
              </Typography>
            </Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="date"
              value={values.endTime || ""}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleChange("endTime", e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 500 }}>
              Chi phí dự kiến (VNĐ)
            </Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="number"
              value={values.cost}
              disabled={!canEdit}
              inputProps={{ min: 0, step: 1000 }}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") { handleChange("cost", 0); return; }
                const n = parseFloat(v);
                if (!isNaN(n)) handleChange("cost", Math.max(0, n));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 500 }}>
              Khoa chủ quản
            </Typography>
            {/* Refactor 2026-05-19: Select → Autocomplete cho phép search. */}
            <Autocomplete
              size="small"
              disabled={!canEdit}
              options={departments}
              getOptionLabel={(o) => o?.name || ""}
              value={departments.find((d) => d.id === values.departmentId) || null}
              onChange={(_, val) => handleChange("departmentId", val?.id || 0)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Tìm khoa..." />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            {/* KPTextfield chỉ render multiline khi prop `height` (minRows) là số. */}
            <KPTextfield
              id="expectedResult"
              label="Kết quả dự kiến"
              value={values.expectedResult}
              disabled={!canEdit}
              height={3}
              handleChangeForm={(_id, val) => handleChange("expectedResult", val)}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard icon={<AttachFile />} title="File đính kèm">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>File thuyết minh đề tài</b>
            </Typography>
            <MultiFileManager
              fileUrls={presentationUrls}
              setFileUrls={setPresentationUrls}
              newFiles={presentationNewFiles}
              setNewFiles={setPresentationNewFiles}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>File thông tin trích ngang (kế hoạch)</b>
            </Typography>
            <MultiFileManager
              fileUrls={statisticUrls}
              setFileUrls={setStatisticUrls}
              newFiles={statisticNewFiles}
              setNewFiles={setStatisticNewFiles}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        icon={<Group />}
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <b>{("Thành viên đề tài").toUpperCase()}</b>
            {canEdit && (
              <Button size="small" variant="outlined" color="primary" startIcon={<Add />} onClick={addParticipant}>
                Thêm thành viên
              </Button>
            )}
          </Box>
        }
      >
        {participants.length === 0 && (
          <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
            Chưa có thành viên. Thêm ít nhất chủ nhiệm đề tài.
          </Typography>
        )}
        {participants.map((p, idx) => (
          <ParticipantRow
            key={idx}
            participant={p}
            profileList={profileList}
            canEdit={canEdit}
            onChange={(patch) => updateParticipant(idx, patch)}
            onRemove={() => removeParticipant(idx)}
          />
        ))}
      </SectionCard>

      {/* History phía dưới cho edit/create mode (nếu là edit của đề tài đã có history) */}
      {!isView && detail?.id && <InternalResearchHistory detail={detail} />}

      {canEdit && (
        <Box marginTop={3} paddingTop={2} display="flex" gridGap={12} justifyContent="flex-end" borderTop="solid 1px #e0e0e0">
          <Button
            variant="outlined" color="primary"
            startIcon={<Save />} disabled={saving}
            onClick={handleSave}
          >
            Lưu nháp
          </Button>
          <Button
            variant="contained" color="primary"
            startIcon={<Send />} disabled={saving}
            onClick={handleSubmit}
          >
            {isAdjustRequest ? "Gửi lại sau chỉnh sửa" : "Nộp đăng ký"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ParticipantRow({ participant, profileList, canEdit, onChange, onRemove }) {
  // Fallback safety: nếu record cũ thiếu field `isPhenikaa` (trước khi BE thêm
  // column is_phenikaa) → derive từ profileId: profileId>0 ⇒ nội bộ, =0 ⇒ ngoài.
  const isPhenikaa = participant.isPhenikaa === undefined
    ? participant.profileId > 0
    : participant.isPhenikaa !== false;
  const selectedProfile = isPhenikaa
    ? profileList.find((p) => p.id === participant.profileId) || null
    : null;

  return (
    <Box
      display="flex" gridGap={8} alignItems="center"
      padding="6px 8px" bgcolor="#fafafa" borderRadius={4}
      marginBottom={6} border="solid 1px #eee"
    >
      <Select
        size="small" variant="outlined" margin="dense"
        value={participant.researchRole || "other"}
        disabled={!canEdit}
        onChange={(e) => onChange({ researchRole: e.target.value })}
        style={{ width: 120 }}
      >
        <MenuItem value="main">Chủ nhiệm</MenuItem>
        <MenuItem value="other">Thành viên</MenuItem>
      </Select>
      {isPhenikaa ? (
        <Autocomplete
          options={profileList}
          getOptionLabel={(o) => `${o.name || ""}${o.degree ? ` (${o.degree})` : ""}`}
          value={selectedProfile}
          disabled={!canEdit}
          onChange={(_, val) =>
            onChange({
              profileId: val?.id || 0,
              name: val?.name || "",
              email: val?.email || "",
              degree: val?.degree || "",
            })
          }
          style={{ flexGrow: 1, minWidth: 240 }}
          renderInput={(params) => (
            <TextField {...params} size="small" variant="outlined" placeholder="Tìm cán bộ..." />
          )}
        />
      ) : (
        <>
          <TextField
            size="small" variant="outlined" placeholder="Họ tên"
            value={participant.name || ""}
            disabled={!canEdit}
            onChange={(e) => onChange({ name: e.target.value })}
            style={{ flexGrow: 1 }}
          />
          <TextField
            size="small" variant="outlined" placeholder="Email"
            value={participant.email || ""}
            disabled={!canEdit}
            onChange={(e) => onChange({ email: e.target.value })}
            style={{ width: 200 }}
          />
        </>
      )}
      <Tooltip title={isPhenikaa ? "Cán bộ nội bộ" : "Ngoài đơn vị"}>
        <Button
          size="small"
          variant={isPhenikaa ? "contained" : "outlined"}
          color="primary"
          disabled={!canEdit}
          onClick={() => onChange({ isPhenikaa: !isPhenikaa, profileId: 0, name: "", degree: "" })}
          style={{ minWidth: 80, height: 32 }}
        >
          {isPhenikaa ? "Nội bộ" : "Ngoài"}
        </Button>
      </Tooltip>
      {canEdit && (
        <Tooltip title="Xoá thành viên">
          <IconButton size="small" onClick={onRemove}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
